/**
 * BreastHealthIntelligenceExportPage.jsx — iter738 (Feb 2026)
 *
 * Print-friendly, single-source-of-truth export view for the BHI module.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The previous export flow was "Ctrl+P" on the interactive dashboard, which
 * produced a PDF full of contradictions:
 *   • Summary card showed a score of 87 while the Progression section still
 *     said "Baseline: —  Change: 0".
 *   • Interactive UI elements ("Move slider", "Click here", "Expand all",
 *     "AI Narration") were captured as static text in the PDF.
 *   • Genetics said "no DNA results" and 2 pages later listed "Genomic
 *     module" recommendations.
 *   • Empty pages, mixed BG/EN labels, broken network graphs.
 *
 * This page fixes all of that by:
 *   1. Reading the SAME `/breast-intelligence/snapshot` payload — one source
 *      of truth for every section shown here and on the interactive page.
 *   2. Rendering `Данни в очакване` state everywhere `insufficient_data`
 *      appears (never fake numbers).
 *   3. Zero interactive controls — no sliders, no tabs, no accordions.
 *   4. Bulgarian-first labels and a clean typographic hierarchy.
 *   5. `@media print` rules that hide the top bar, keep sections together,
 *      and format for A4.
 *
 * The user prints via the browser dialog (⌘/Ctrl+P → Save as PDF). No
 * server-side rendering pipeline is required.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Printer, ArrowLeft, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import SEO from "@/components/SEO";
import EducationalRibbon from "@/components/EducationalRibbon";
import BhiFullDossierExportDialog from "@/components/breast_intel/BhiFullDossierExportDialog";
// iter878 — useMethodology removed (composite risk calculator deleted).

/* ──────────────────────────────────────────────────────────────────
   BG label maps — kept in one place so every export renders the same
   ────────────────────────────────────────────────────────────────── */
const ZONE_LABELS = {
  epithelium: "Епителна среда",
  stroma:     "Строма (съединителна тъкан)",
  adipose:    "Мастна тъкан",
  immune:     "Имунна среда",
  vascular:   "Съдова среда",
  lymphatic:  "Лимфна среда",
};

const COMPONENT_LABELS = {
  insulin:      "Инсулинов натиск",
  igf1:         "IGF-1 сигнал",
  inflammation: "Възпалителен товар",
  estrogen:     "Естрогенен сигнал",
  adiposity:    "Адипозитет",
};

const BAND_LABELS = {
  excellent:         "Отличен",
  good:              "Добър",
  moderate:          "Умерен",
  needs_attention:   "Изисква внимание",
  attention:         "Изисква внимание",
  poor:              "Незадоволителен",
  green:             "Добър",
  yellow:            "Умерен",
  red:               "Изисква внимание",
  insufficient_data: "Данни в очакване",
};

const BAND_COLORS = {
  excellent:         "#059669",
  good:              "#16a34a",
  moderate:          "#ca8a04",
  needs_attention:   "#dc2626",
  attention:         "#dc2626",
  poor:              "#7f1d1d",
  green:             "#16a34a",
  yellow:            "#ca8a04",
  red:               "#dc2626",
  insufficient_data: "#6b7280",
};

const PENDING_MSG = "Данни в очакване — качете лабораторни резултати или попълнете Health Assessment.";

/* ──────────────────────────────────────────────────────────────────
   Small utility renderers
   ────────────────────────────────────────────────────────────────── */
function ScoreBadge({ score, band }) {
  const isPending = score == null || band === "insufficient_data";
  const label = isPending ? "—" : `${Math.round(score)}/100`;
  const bandLabel = BAND_LABELS[band] || band || "—";
  const color = BAND_COLORS[band] || "#6b7280";
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium"
      style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}30` }}
    >
      <span>{label}</span>
      <span className="opacity-70">·</span>
      <span>{bandLabel}</span>
    </span>
  );
}

function Section({ title, subtitle, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bhi-export-section mb-8 print:mb-6 print:break-inside-avoid"
    >
      <header className="mb-4 border-b border-slate-200 pb-2">
        <h2 className="text-2xl font-bold text-slate-900 print:text-xl">{title}</h2>
        {subtitle ? (
          <p className="mt-1 text-sm text-slate-500 print:text-xs">{subtitle}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function Pending({ note }) {
  return (
    <div
      data-testid="pending-state"
      className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 print:bg-white"
    >
      <div className="font-medium text-slate-700">Данни в очакване</div>
      <div className="mt-0.5 text-slate-500">{note || PENDING_MSG}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 border-b border-slate-100 last:border-b-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800">{value}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────── */
export default function BreastHealthIntelligenceExportPage() {
  const { user, API, loading: authLoading } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // iter878 — methodology hook removed (composite risk calculator deleted).

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const [snapRes, aiRes] = await Promise.allSettled([
          axios.get(`${API}/breast-intelligence/snapshot`),
          axios.get(`${API}/breast-intelligence/ai-summary`),
        ]);
        if (!alive) return;
        if (snapRes.status === "fulfilled") setSnapshot(snapRes.value.data);
        else setErr(snapRes.reason?.message || "Snapshot грешка");
        if (aiRes.status === "fulfilled") setAiSummary(aiRes.value.data?.summary || null);
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authLoading, user, API]);

  const generatedAt = useMemo(() => {
    if (!snapshot?.generated_at) return "";
    try {
      const d = new Date(snapshot.generated_at);
      return d.toLocaleString("bg-BG", { dateStyle: "long", timeStyle: "short" });
    } catch { return snapshot.generated_at; }
  }, [snapshot]);

  const patientName = useMemo(() => {
    if (!user) return "";
    return user.name || user.email?.split("@")[0] || "Пациент";
  }, [user]);

  /* Auth gate */
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen bg-white p-8 text-slate-800">
        <SEO title="BHI Export — Nutramilo" description="Печатна версия на Breast Health Intelligence" path="/health-intelligence/breast/export" />
        <div className="mx-auto max-w-md rounded-lg border border-slate-200 p-6 text-center">
          <p className="mb-4 text-sm text-slate-600">
            Влезте в акаунта си, за да генерирате експорт на BHI отчета.
          </p>
          <Link to="/" className="text-sm font-medium text-indigo-600 hover:underline">Към входа</Link>
        </div>
      </div>
    );
  }

  if (loading || !snapshot) {
    return (
      <div className="min-h-screen bg-white p-8 text-slate-800">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
          <span className="text-sm text-slate-600">Зареждане на BHI snapshot...</span>
        </div>
        {err ? <p className="mt-4 text-sm text-red-600">Грешка: {err}</p> : null}
      </div>
    );
  }

  const biology       = snapshot.biology       || {};
  const metabolic     = snapshot.metabolic     || {};
  const interventions = snapshot.interventions || {};
  const density       = snapshot.density       || {};
  const tissueAge     = snapshot.tissue_age    || {};
  const signals       = snapshot.signals       || {};
  const attribution   = snapshot.twin_attribution || {};
  const registry      = attribution.twin_registry || {};

  const metabolicComponents = metabolic.components || {};
  const metabolicTotal = metabolic.total;

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      <SEO title="BHI Export — Nutramilo" description="Печатна версия на Breast Health Intelligence отчет" path="/health-intelligence/breast/export" />

      {/* Top toolbar — hidden in print */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur print:hidden">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link
            to="/health-intelligence/breast"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            data-testid="export-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Обратно към BHI
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="export-print-button"
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
          >
            <Printer className="h-4 w-4" /> Печат / Запази PDF
          </button>
        </div>
      </div>

      {/* iter943 — server-generated multi-page dossier PDF, между toolbar и main */}
      <div className="mx-auto max-w-4xl px-4 pt-3 print:hidden" data-testid="dossier-server-pdf-toolbar">
        <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3">
          <div className="text-xs text-slate-700">
            <div className="font-semibold text-slate-900">Server-generated PDF</div>
            <div className="text-slate-600">Двуезичен визуален отчет с всички BHI модули (multi-page).</div>
          </div>
          <BhiFullDossierExportDialog variant="ghost" />
        </div>
      </div>

      <main
        data-testid="bhi-export-root"
        className="mx-auto max-w-4xl bg-white px-6 py-10 text-slate-800 shadow-sm print:max-w-none print:px-0 print:py-0 print:shadow-none"
      >
        {/* Header */}
        <header className="mb-8 border-b-2 border-slate-900 pb-6 print:mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Nutramilo · Digital Health Twin</div>
              <h1 className="mt-1 text-3xl font-bold text-slate-900 print:text-2xl">
                Метаболитен профил на гръдна тъкан
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Образователен отчет — тъканни среди, плътност (ръчно) и метаболитни сигнали. НЕ е диагностика.
              </p>
            </div>
            <div className="text-right text-xs text-slate-500">
              <div>Пациент: <span className="font-medium text-slate-800">{patientName}</span></div>
              {generatedAt ? <div>Генерирано: {generatedAt}</div> : null}
            </div>
          </div>
        </header>

        {/* Educational-only ribbon — high-contrast, shown at the top of every PDF */}
        <EducationalRibbon
          lang="bg"
          variant="print"
          className="mb-6 print:mb-4"
          testId="bhi-export-educational-ribbon"
        />

        {/* Executive Summary Card — ONE source of truth */}
        <Section
          title="Изпълнително обобщение"
          subtitle="Обобщен индекс и ориентировъчна метаболитна възраст — единен източник"
          testId="section-summary"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Метаболитен профил (индекс 0–100)</div>
              <div className="mt-2">
                {metabolicTotal == null ? (
                  <Pending />
                ) : (
                  <div>
                    <div className="text-4xl font-bold text-slate-900 print:text-3xl">
                      {Math.round(metabolicTotal)}<span className="text-lg text-slate-400">/100</span>
                    </div>
                    <div className="mt-1"><ScoreBadge score={metabolicTotal} band={metabolic.band} /></div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Ориентировъчна метаболитна възраст</div>
              <div className="mt-2">
                {tissueAge?.tissue_age == null ? (
                  <Pending note="Недостатъчно антропометрични/лабораторни данни за изчисление." />
                ) : (
                  <div>
                    <div className="text-4xl font-bold text-slate-900 print:text-3xl">
                      {Math.round(tissueAge.tissue_age)} <span className="text-lg text-slate-400">г.</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      Хронологична възраст: {signals.age ? `${signals.age} г.` : "—"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {aiSummary ? (
            <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-4 text-sm leading-relaxed text-slate-700 print:bg-white">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-indigo-700">
                <Sparkles className="h-3.5 w-3.5" /> AI образователно обобщение
              </div>
              <div className="whitespace-pre-wrap">{aiSummary}</div>
            </div>
          ) : null}
        </Section>

        {/* Tissue Biology */}
        <Section title="1. Тъканни среди (образователен индекс)" subtitle="6 тъканни среди — на база лабораторни и lifestyle сигнали. Не измерва тъкан." testId="section-biology">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {Object.keys(ZONE_LABELS).map((k) => {
              const z = biology[k] || {};
              return (
                <div key={k} className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900">{ZONE_LABELS[k]}</div>
                    <ScoreBadge score={z.score} band={z.band} />
                  </div>
                  {z.band === "insufficient_data" || z.score == null ? (
                    <div className="mt-2 text-xs text-slate-500">{PENDING_MSG}</div>
                  ) : (
                    <ul className="mt-2 list-inside list-disc text-xs text-slate-600">
                      {(z.factors || []).slice(0, 3).map((f, i) => (
                        <li key={i}>{typeof f === "string" ? f : f.title || f.label || ""}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* Density Intelligence */}
        <Section title="2. Плътност на тъканта (ръчно въведен BI-RADS)" subtitle="Плътността НЕ се измерва автоматично — идва от Вашия радиологичен доклад." testId="section-density">
          {density.density_percent == null && !density.birads ? (
            <Pending note="Няма въведена BI-RADS плътност. Можете да я добавите ръчно на страницата BHI → My Data Hub." />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <KV label="BI-RADS" value={density.birads || "—"} />
              <KV label="Плътност %" value={density.density_percent != null ? `${density.density_percent}%` : "—"} />
              <KV label="Категория" value={density.band ? BAND_LABELS[density.band] || density.band : "—"} />
            </div>
          )}
        </Section>

        {/* Metabolic Environment */}
        <Section title="3. Метаболитен профил" subtitle="5 компонента — инсулинов, IGF-1, възпалителен, естрогенен, адипозен. Индекс, не диагноза." testId="section-metabolic">
          {metabolicTotal == null ? (
            <Pending note={metabolic._evidence_note || PENDING_MSG} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {Object.keys(COMPONENT_LABELS).map((k) => {
                const c = metabolicComponents[k] || {};
                return (
                  <div key={k} className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-900">{COMPONENT_LABELS[k]}</div>
                      <ScoreBadge score={c.score} band={c.band} />
                    </div>
                    {c.band === "insufficient_data" || c.score == null ? (
                      <div className="mt-2 text-xs text-slate-500">{PENDING_MSG}</div>
                    ) : c.evidence_id ? (
                      <div className="mt-2 text-xs text-slate-500">Evidence: {c.evidence_id}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Interventions */}
        <Section title="4. Персонализирани интервенции" subtitle="Хранене, ум и тяло, движение, суплементи — на база текущата среда" testId="section-interventions">
          {(() => {
            const groups = [
              ["nutrition", "Хранене"],
              ["mindbody", "Ум и тяло"],
              ["activity", "Активност"],
              ["supplements", "Суплементи"],
            ];
            const anyItems = groups.some(([k]) => (interventions[k] || []).length > 0);
            if (!anyItems) return <Pending note="Няма достатъчно данни за персонализирани препоръки. Попълнете дневник и/или качете лаборатории." />;
            return (
              <div className="space-y-4">
                {groups.map(([k, label]) => {
                  const arr = interventions[k] || [];
                  if (!arr.length) return null;
                  return (
                    <div key={k}>
                      <div className="mb-1 text-sm font-semibold text-slate-900">{label}</div>
                      <ul className="list-inside list-disc space-y-1 text-sm text-slate-700">
                        {arr.map((it, i) => (
                          <li key={i}>
                            <span className="font-medium">{it.title || it.label || ""}</span>
                            {it.rationale ? <span className="text-slate-500"> — {it.rationale}</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Section>

        {/* Twin Attribution — what data feeds this report */}
        <Section title="5. Дигитални близнаци, захранващи този отчет" subtitle="Прозрачност — кой източник дава сигнал за коя секция" testId="section-attribution">
          <div className="space-y-2 text-sm text-slate-700">
            {Object.entries(registry).map(([id, meta]) => (
              <div key={id} className="flex items-baseline justify-between gap-4 border-b border-slate-100 pb-1 last:border-b-0">
                <span className="font-medium text-slate-900">{meta.label_bg || id}</span>
                <span className="text-xs text-slate-500">{id}</span>
              </div>
            ))}
          </div>
          {attribution.note_bg ? (
            <p className="mt-3 text-xs italic text-slate-500">{attribution.note_bg}</p>
          ) : null}
        </Section>

        {/* Signals snapshot */}
        <Section title="6. Използвани сигнали за този snapshot" testId="section-signals">
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            <KV label="Хранения (14 дни)" value={signals.meals_last_14d ?? "—"} />
            <KV label="NIS среден" value={signals.nis_avg ?? "—"} />
            <KV label="DIL среден" value={signals.dil_avg ?? "—"} />
            <KV label="BMI" value={signals.bmi || "—"} />
            <KV label="Възраст" value={signals.age ?? "—"} />
            <KV label="Менопауза" value={signals.menopause ? "Да" : "Не"} />
            <KV label="BI-RADS въведен" value={signals.has_density_birads ? "Да" : "Не"} />
            <KV label="Семейна анамнеза" value={signals.family_history ? "Да" : "Не"} />
            <KV label="Пушене" value={signals.smoking ? "Да" : "Не"} />
            <KV label="Алкохол (единици/седмица)" value={signals.alcohol_units_per_week ?? "—"} />
            <KV label="Движение (мин/седмица)" value={signals.exercise_minutes_per_week ?? "—"} />
            <KV label="Сън (часа)" value={signals.sleep_hours ?? "—"} />
            <KV label="Стрес (0-10)" value={signals.stress_level ?? "—"} />
          </div>
        </Section>

        {/* Disclaimer */}
        <footer className="mt-8 border-t border-slate-200 pt-4 text-xs leading-relaxed text-slate-500 print:mt-6">
          <div className="mb-2 flex items-center gap-2 font-medium text-slate-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Важна информация
          </div>
          <p>
            Този отчет е <strong>образователен</strong> и е предназначен за информационни цели.
            НЕ е диагностика, лечение или прогноза за рак на гърдата. Отразява които може да променим
            lifestyle/метаболитни среди — не измерва тъкан и не предвижда заболяване.
            Винаги се консултирайте с квалифициран лекар.
          </p>
          {snapshot.context_sources && snapshot.context_sources.length ? (
            <p className="mt-2">
              Използвани източници на данни: {snapshot.context_sources.join(", ")}
            </p>
          ) : null}
          <p className="mt-2 text-[10px] opacity-70">
            Nutramilo · Digital Health Twin · Report ID: {snapshot.generated_at || ""}
          </p>
          {/* iter878 — Methodology-version footer removed together with composite risk calculator. */}
        </footer>
      </main>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          html, body { background: #ffffff !important; }
          .bhi-export-section { break-inside: avoid; page-break-inside: avoid; }
          h1, h2, h3 { break-after: avoid; page-break-after: avoid; }
          a { color: inherit !important; text-decoration: none !important; }
        }
      `}</style>
    </div>
  );
}
